import { FC } from "react";
import SectionHero from "./SectionHero";
import { globalJson } from "global/global_json";
import SiteHeader from "../SiteHeader";
import FAQ from "designs/DesignB/components/FAQ/FAQ";
import AboutSection from "designs/DesignB/components/AboutSection/AboutSection";
import { useData } from "data/data-provider";
import { isUsableUrl } from "utils/isUsableUrl";

export interface PageAboutProps {
  className?: string;
}

const PageAbout: FC<PageAboutProps> = () => {
  // Use the About Us image from the website builder (the same backend source
  // the home page's About section uses) instead of a bundled static asset.
  // The old `images/about.png` import didn't exist and broke the build once
  // this page got routed (1NOW-1252).
  const { aboutUs, banner } = useData();

  // The top hero is a full-screen banner with the breadcrumb/heading
  // (AboutSection) overlaid in white. Without a background image it rendered
  // as an empty area with invisible white text. Paint the SAME banner the
  // home page uses — the desktop image on lg+, the mobile image below lg —
  // so the About hero looks and behaves exactly like the home page banner
  // (1NOW-1252). Guard against literal "undefined"/"null" URLs like PageHome.
  const desktopBannerBg = isUsableUrl(banner?.image_url)
    ? banner.image_url
    : "";
  const mobileBannerBgCandidate = banner?.mobile_image_url || banner?.image_url;
  const mobileBannerBg = isUsableUrl(mobileBannerBgCandidate)
    ? mobileBannerBgCandidate
    : "";

  // Header + heading + scroll cue overlaid on the banner. Rendered inside
  // both the desktop and mobile banner shells below (only one shows at a
  // time) so the only thing that differs is which banner image is painted —
  // same approach as PageHome.
  const heroOverlay = (
    <>
      <SiteHeader />
      <div className="absolute top-1/4 left-0 right-0 z-10">
        <AboutSection />
      </div>
      <div className="absolute bottom-0 left-0 right-0 flex justify-center z-10">
        <a
          onClick={(e) => {
            e.preventDefault();
            const section = document.getElementById("section");
            if (section) {
              section.scrollIntoView({ behavior: "smooth" });
            }
          }}
          href="#"
          className="cursor-pointer"
        >
          <svg
            width="254"
            height="31"
            viewBox="0 0 254 31"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M66.1635 0C58.8803 0 52.1108 1.72068 48.2507 4.55314L18.4127 26.4469C14.5526 29.2793 7.78313 31 0.5 31H127.499L127.5 0H66.1635Z"
              fill="currentColor"
              className="text-primary-100"
            />
            <path
              d="M187.837 0C195.12 0 201.889 1.72068 205.749 4.55314L235.587 26.4469C239.447 29.2793 246.217 31 253.5 31H126.501L126.5 0H187.837Z"
              fill="currentColor"
              className="text-primary-100"
            />
            <path
              d="M92.034 19V11.3H93.464V17.68H97.017V19H92.034ZM98.6512 19V11.3H103.689V12.598H100.059V17.702H103.733V19H98.6512ZM99.3112 15.7V14.424H103.128V15.7H99.3112ZM107.066 19V12.62H104.866V11.3H110.762V12.62H108.496V19H107.066ZM111.571 14.16L111.813 11.3H113.287L112.385 14.16H111.571ZM117.035 19.11C116.587 19.11 116.177 19.055 115.803 18.945C115.429 18.8277 115.091 18.6553 114.791 18.428C114.49 18.2007 114.222 17.9257 113.988 17.603L114.923 16.547C115.282 17.0457 115.634 17.3903 115.979 17.581C116.323 17.7717 116.712 17.867 117.145 17.867C117.394 17.867 117.621 17.8303 117.827 17.757C118.032 17.6763 118.193 17.57 118.311 17.438C118.428 17.2987 118.487 17.141 118.487 16.965C118.487 16.8403 118.461 16.7267 118.41 16.624C118.366 16.514 118.296 16.4187 118.201 16.338C118.105 16.25 117.988 16.1693 117.849 16.096C117.709 16.0227 117.552 15.9603 117.376 15.909C117.2 15.8577 117.005 15.81 116.793 15.766C116.389 15.6853 116.037 15.579 115.737 15.447C115.436 15.3077 115.183 15.139 114.978 14.941C114.772 14.7357 114.622 14.5083 114.527 14.259C114.431 14.0023 114.384 13.7127 114.384 13.39C114.384 13.0673 114.453 12.7703 114.593 12.499C114.739 12.2277 114.937 11.993 115.187 11.795C115.436 11.597 115.726 11.443 116.056 11.333C116.386 11.223 116.741 11.168 117.123 11.168C117.555 11.168 117.94 11.2193 118.278 11.322C118.622 11.4247 118.923 11.5787 119.18 11.784C119.444 11.982 119.66 12.224 119.829 12.51L118.883 13.445C118.736 13.2177 118.571 13.0307 118.388 12.884C118.204 12.73 118.006 12.6163 117.794 12.543C117.581 12.4623 117.357 12.422 117.123 12.422C116.859 12.422 116.628 12.4587 116.43 12.532C116.239 12.6053 116.089 12.7117 115.979 12.851C115.869 12.983 115.814 13.1443 115.814 13.335C115.814 13.4817 115.847 13.6137 115.913 13.731C115.979 13.841 116.07 13.94 116.188 14.028C116.312 14.116 116.47 14.193 116.661 14.259C116.851 14.325 117.068 14.3837 117.31 14.435C117.713 14.5157 118.076 14.6257 118.399 14.765C118.721 14.897 118.996 15.0583 119.224 15.249C119.451 15.4323 119.623 15.645 119.741 15.887C119.858 16.1217 119.917 16.3857 119.917 16.679C119.917 17.185 119.796 17.6213 119.554 17.988C119.319 18.3473 118.985 18.626 118.553 18.824C118.12 19.0147 117.614 19.11 117.035 19.11ZM128.22 19.11C127.641 19.11 127.106 19.011 126.614 18.813C126.123 18.6077 125.694 18.3253 125.327 17.966C124.968 17.6067 124.689 17.1887 124.491 16.712C124.293 16.228 124.194 15.7037 124.194 15.139C124.194 14.5817 124.293 14.0647 124.491 13.588C124.697 13.104 124.983 12.686 125.349 12.334C125.716 11.9747 126.145 11.696 126.636 11.498C127.135 11.3 127.67 11.201 128.242 11.201C128.646 11.201 129.034 11.256 129.408 11.366C129.782 11.476 130.12 11.63 130.42 11.828C130.721 12.026 130.97 12.2497 131.168 12.499L130.266 13.445C130.061 13.247 129.848 13.082 129.628 12.95C129.416 12.818 129.192 12.7153 128.957 12.642C128.723 12.5687 128.481 12.532 128.231 12.532C127.865 12.532 127.524 12.598 127.208 12.73C126.9 12.862 126.629 13.0453 126.394 13.28C126.167 13.5147 125.987 13.7897 125.855 14.105C125.723 14.4203 125.657 14.765 125.657 15.139C125.657 15.4983 125.723 15.8393 125.855 16.162C125.995 16.4847 126.185 16.767 126.427 17.009C126.669 17.251 126.948 17.4417 127.263 17.581C127.586 17.713 127.927 17.779 128.286 17.779C128.558 17.779 128.811 17.7387 129.045 17.658C129.28 17.5773 129.489 17.4673 129.672 17.328C129.856 17.1813 129.995 17.0127 130.09 16.822C130.193 16.6313 130.244 16.426 130.244 16.206V15.964L130.442 16.195H128.165V14.919H131.652C131.667 14.9997 131.678 15.0913 131.685 15.194C131.693 15.2893 131.696 15.3847 131.696 15.48C131.704 15.568 131.707 15.645 131.707 15.711C131.707 16.217 131.619 16.679 131.443 17.097C131.267 17.5077 131.018 17.8633 130.695 18.164C130.38 18.4647 130.01 18.6993 129.584 18.868C129.166 19.0293 128.712 19.11 128.22 19.11ZM136.531 19.11C135.981 19.11 135.471 19.011 135.002 18.813C134.532 18.615 134.122 18.34 133.77 17.988C133.425 17.6287 133.157 17.207 132.967 16.723C132.776 16.239 132.681 15.711 132.681 15.139C132.681 14.567 132.776 14.039 132.967 13.555C133.157 13.071 133.425 12.653 133.77 12.301C134.122 11.9417 134.532 11.663 135.002 11.465C135.471 11.267 135.981 11.168 136.531 11.168C137.088 11.168 137.601 11.267 138.071 11.465C138.54 11.663 138.947 11.9417 139.292 12.301C139.636 12.6603 139.904 13.082 140.095 13.566C140.285 14.0427 140.381 14.567 140.381 15.139C140.381 15.7037 140.285 16.228 140.095 16.712C139.904 17.196 139.636 17.6177 139.292 17.977C138.947 18.3363 138.54 18.615 138.071 18.813C137.601 19.011 137.088 19.11 136.531 19.11ZM136.531 17.746C136.883 17.746 137.202 17.6837 137.488 17.559C137.781 17.427 138.034 17.2437 138.247 17.009C138.459 16.767 138.624 16.4883 138.742 16.173C138.866 15.8577 138.929 15.513 138.929 15.139C138.929 14.765 138.866 14.4203 138.742 14.105C138.624 13.7897 138.459 13.5147 138.247 13.28C138.034 13.038 137.781 12.8547 137.488 12.73C137.202 12.598 136.883 12.532 136.531 12.532C136.186 12.532 135.867 12.598 135.574 12.73C135.28 12.8547 135.027 13.0343 134.815 13.269C134.602 13.5037 134.437 13.7787 134.32 14.094C134.202 14.4093 134.144 14.7577 134.144 15.139C134.144 15.513 134.202 15.8613 134.32 16.184C134.437 16.4993 134.602 16.7743 134.815 17.009C135.027 17.2437 135.28 17.427 135.574 17.559C135.867 17.6837 136.186 17.746 136.531 17.746Z"
              fill="currentColor"
              className="secondary-text-color"
            />
            <path
              d="M154 22.1667C157.682 22.1667 160.667 19.1819 160.667 15.5C160.667 11.8181 157.682 8.83334 154 8.83334C150.318 8.83334 147.333 11.8181 147.333 15.5C147.333 19.1819 150.318 22.1667 154 22.1667Z"
              fill="currentColor"
              className="secondary-text-color"
            />
            <path
              d="M152.333 16.5C152.661 16.8371 153.533 18.1667 154 18.1667M154 18.1667C154.467 18.1667 155.339 16.8371 155.667 16.5M154 18.1667V12.8333"
              stroke="white"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop banner — desktop banner image; hidden on mobile. */}
      <div
        className="hidden lg:!flex justify-between flex-col bg-cover bg-center w-full h-screen relative"
        style={{
          backgroundImage: desktopBannerBg
            ? `url(${desktopBannerBg})`
            : undefined,
          backgroundSize: banner?.position ?? "cover",
          backgroundPosition: banner?.image_alignment ?? "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {heroOverlay}
      </div>
      {/* Mobile banner — mobile banner image, falling back to the desktop
          image when none is set (mirrors PageHome); hidden on desktop. */}
      <div
        className="flex lg:!hidden justify-between flex-col bg-cover bg-center w-full h-[100dvh] relative"
        style={{
          backgroundImage: mobileBannerBg
            ? `url(${mobileBannerBg})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: banner?.image_alignment ?? "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {heroOverlay}
      </div>
      <div id="section" className="container pt-16  space-y-16 ">
        <SectionHero
          rightImg={aboutUs?.image_url || ""}
          heading={aboutUs?.heading?.trim() || "About Us"}
          subHeading={aboutUs?.description?.trim()}
          btnText="Learn More"
        />
      </div>

      <div id="faq">
        <FAQ />
      </div>
    </>
  );
};

export default PageAbout;
